import { cx } from '@linaria/core';
import { useId, useRef, useEffect, useCallback, useState } from 'react';
import { resets } from '@ui/css';
import { throttleToAnimationFrame } from '@utils/common';
import { useToggleState, useKatex, Chevron } from '@utils/react';
import { Dropdown } from '../overlays/dropdown/dropdown.js';
import { InputBorder } from './atom.input-border.js';
import { Listbox } from './listbox/_listbox.js';
import { getOptionLabel } from './listbox/option.listbox.js';
import { chevronWrapper, button, listboxWrapper, headingWrapper, headingSeparator, listbox, wrapper, modifiers } from './select.style.js';
import { jsxs, jsx } from 'react/jsx-runtime';
import { Tooltip } from '../overlays/tooltip/tooltip.js';

function Select(props) {
  const labelId = `label-${useId()}`;
  const listboxId = `listbox-${useId()}`;
  const buttonRef = useRef(null);
  const triggerRef = useRef(null);
  const headerRef = useRef(null);
  const [showListbox, listboxCallbacks] = useToggleState();
  const value = props.selectedValues?.map(value => typeof value === "object" ? getOptionLabel(value) : getOptionLabel(props.options.find(it => typeof it === "object" ? it.id === value : it === value) ?? value)).join(", ") ?? "";
  const width = useSelectWidth(triggerRef, showListbox);
  useKatex(buttonRef, [value]);

  /**
   * Focus will be caught by the listbox when it is enabled, however on
   * Tab/Escape we want to ensure that focus is restored to the select button
   */
  useEffect(() => {
    if (!showListbox) {
      return;
    }
    const handleKeydown = evt => {
      switch (evt.key) {
        case "Enter":
        case " ":
          {
            if (!props.multiSelect) {
              listboxCallbacks.disable();
            }
            break;
          }
        case "Tab":
          {
            if (headerRef.current?.contains(document.activeElement) || evt.shiftKey) {
              return;
            }
            listboxCallbacks.disable();
            break;
          }
      }
    };
    window.addEventListener("keydown", handleKeydown, {
      capture: true
    });
    return () => {
      window.removeEventListener("keydown", handleKeydown, {
        capture: true
      });
    };
  }, [listboxCallbacks, props.multiSelect, showListbox]);
  const onToggleValue = useCallback((value, option) => {
    props.toggleValue?.(value, option);
    if (props.collapseOnClick) {
      listboxCallbacks.disable();
    }
  }, [listboxCallbacks, props]);
  const onClickInside = useCallback(() => {
    if (showListbox && props.collapseOnClick) {
      listboxCallbacks.disable();
    }
  }, [listboxCallbacks, props.collapseOnClick, showListbox]);
  return /*#__PURE__*/jsxs("div", {
    className: cx(props.className, resets.rootCls, wrapper, showListbox && modifiers.listboxOpen.className, props.disabled && modifiers.disabled.className),
    "data-theme": props.theme,
    children: [/*#__PURE__*/jsx(Tooltip, {
      disabled: !props.tooltip || showListbox,
      containerRef: triggerRef,
      placement: props.tooltip?.placement ?? "right-start",
      className: props.tooltip?.className,
      body: props.tooltip?.body,
      ariaType: props.tooltip?.ariaType,
      children: ref => /*#__PURE__*/jsx(InputBorder, {
        ...props,
        className: undefined,
        value: value,
        ref: ref,
        active: showListbox,
        readOnly: props.disabled,
        label: props.theme === "opgavekomplekset" ? undefined : props.label,
        children: /*#__PURE__*/jsxs("button", {
          ref: buttonRef,
          className: cx(button, props.theme === "opgavekomplekset" && !value && "dimmed"),
          onClick: () => !props.disabled && listboxCallbacks.toggle(),
          type: "button",
          "aria-haspopup": "listbox",
          id: labelId,
          "aria-expanded": showListbox,
          "aria-controls": listboxId,
          disabled: props.disabled,
          children: [/*#__PURE__*/jsx("span", {
            children: value || (props.theme === "opgavekomplekset" ? props.label : undefined)
          }), /*#__PURE__*/jsx("div", {
            className: chevronWrapper,
            children: /*#__PURE__*/jsx(Chevron, {
              vertical: true,
              inversed: showListbox,
              size: 10,
              lineWidth: 1
            })
          })]
        })
      })
    }), /*#__PURE__*/jsx(Dropdown, {
      className: props.dropdownClassName,
      active: showListbox,
      triggerRef: triggerRef,
      placement: props.dropdownPlacement ?? "bottom-start",
      onClose: listboxCallbacks.disable,
      offsetPx: props.dropdownOffsetPx ?? 10,
      children: /*#__PURE__*/jsxs("div", {
        onClick: onClickInside,
        className: listboxWrapper,
        style: {
          minWidth: width
        },
        children: [props.header && /*#__PURE__*/jsxs("div", {
          ref: headerRef,
          className: headingWrapper,
          children: [props.header, /*#__PURE__*/jsx("hr", {
            className: headingSeparator
          })]
        }), /*#__PURE__*/jsx(Listbox, {
          id: listboxId,
          selectedValues: props.selectedValues,
          labelledBy: labelId,
          className: listbox,
          options: props.options,
          multiSelect: props.multiSelect,
          toggleValue: onToggleValue,
          onChange: props.onChange,
          defaultIcon: props.defaultIcon,
          toggledIcon: props.toggledIcon,
          theme: props.listboxTheme
        })]
      })
    })]
  });
}
function useSelectWidth(wrapperRef, active) {
  const [width, setWidth] = useState(() => wrapperRef.current?.clientWidth);
  useEffect(() => {
    if (!active) {
      return;
    }
    const update = () => {
      setWidth(wrapperRef.current?.clientWidth);
    };
    update();
    const throttled = throttleToAnimationFrame(update);
    window.addEventListener("resize", throttled);
    window.addEventListener("orientationchange", throttled);
    return () => {
      window.removeEventListener("resize", throttled);
      window.removeEventListener("orientationchange", throttled);
    };
  }, [active, wrapperRef]);
  return width;
}

export { Select };
//# sourceMappingURL=select.js.map
